import axios from "axios";
import { PromiseHandler } from "../../../handlers";
import { parseIdFilter } from "../../../handlers/filter";
import { isServicePage } from "../../../handlers/page";
import { NotImplementedError } from "../../../errors";
import { validators } from "./validators";
import { pricingAdvertiserFromService, pricingAdvertiserBillingInfoFromService, upsertAdvertiserCpmRequestToService } from "../../../models/verticalBilling";
import { AdvertiserPricingFetchFailed, AdvertiserPricingSaveFailed } from "./errors";
import { toAdvertiserDetailQueryParams, toAdvertiserPricingQueryParams } from "../filters";
const REQUEST_CONFIG = { headers: { "Content-Type": "application/json" } };
/**
 * Advertiser-level retail CPMs for vertical contracts.
 * A single id filter returns the advertiser detail with its product rates;
 * any other find returns the paginated, searchable advertiser list. Search also
 * matches the names of the advertiser's agencies.
 */
class PricingAdvertisers extends PromiseHandler {
    constructor(sdk) {
        super(sdk, "pricing-advertisers", { atomize: true });
        this.url = `${this.sdk.urls.baseAPIUrl}/vertical-contracts`;
        this.validators = validators;
    }
    async findItems(filters = {}, sort) {
        const idFilter = parseIdFilter(filters);
        try {
            return idFilter.size === 1
                ? await this.getAdvertiserItems(idFilter.values().next().value)
                : await this.getAdvertiserList(filters, sort);
        }
        catch (error) {
            throw new AdvertiserPricingFetchFailed(error);
        }
    }
    /**
     * @param filters - Search and pagination for the advertiser list.
     * @param sort - Optional sort configuration.
     * @return A promise resolving to advertisers, or a Page of advertisers when the response is paginated.
     */
    getAdvertiserList(filters, sort) {
        const url = `${this.url}/advertisers${toAdvertiserPricingQueryParams(filters, sort)}`;
        const pageSize = filters.paging?.size || 50;
        // List rows have no billingInfo, so they must not replace cached details.
        return this.cache.promise(url, () => axios
            .get(url, REQUEST_CONFIG)
            .then(({ data: res }) => {
            const advertisers = res.data?.map(pricingAdvertiserFromService) || [];
            if (isServicePage(res)) {
                return {
                    page: {
                        count: res.paging_info.count,
                        token: res.paging_info.token,
                        size: pageSize
                    },
                    data: advertisers
                };
            }
            return advertisers;
        }), { atomize: false });
    }
    /**
     * @param advertiserId - The id of the advertiser to get.
     * @param filters - Optional filters for search and pagination of nested products.
     * @param sort - Optional sort configuration for nested products (name, cpm).
     * @return A promise resolving to advertiser with billing info and pagination info.
     */
    async getAdvertiser(advertiserId, filters, sort) {
        const url = `${this.detailUrl(advertiserId)}${toAdvertiserDetailQueryParams(filters, sort)}`;
        return axios
            .get(url, REQUEST_CONFIG)
            .then(({ data: res }) => ({
            advertiser: pricingAdvertiserBillingInfoFromService(res.data),
            totalCount: res.paging_info?.count ?? 0,
            pageToken: res.paging_info?.token
        }))
            .catch((error) => {
            throw new AdvertiserPricingFetchFailed(error);
        });
    }
    /**
     * @param advertiserId - The id of the advertiser to get.
     * @return A promise resolving to the advertiser, with billingInfo, as a single-item list.
     */
    getAdvertiserItems(advertiserId) {
        const url = this.detailUrl(advertiserId);
        return this.cache.promise(url, () => this.fetchAdvertiser(advertiserId).then((advertiser) => [advertiser]));
    }
    fetchAdvertiser(advertiserId) {
        return axios
            .get(this.detailUrl(advertiserId), REQUEST_CONFIG)
            .then(({ data: res }) => pricingAdvertiserBillingInfoFromService(res.data));
    }
    detailUrl(advertiserId) {
        return `${this.url}/advertiser/${advertiserId}`;
    }
    /**
     * Validates and upserts the advertiser's CPMs, then refetches the detail since the endpoint returns no data.
     * billingInfo.products should hold only the changed rows; a row without a cpm deletes that rate.
     * @param advertiser - Advertiser with the changed product rows.
     * @return The refreshed advertiser with billingInfo.
     */
    async saveItem(advertiser, { skipValidation } = {}) {
        if (!skipValidation) {
            const errors = this.validate(advertiser);
            if (errors.size) {
                throw errors;
            }
        }
        const request = upsertAdvertiserCpmRequestToService(advertiser);
        if (!request.upserts.length) {
            throw new AdvertiserPricingSaveFailed(null, "No product CPMs to update.");
        }
        try {
            await axios.post(`${this.url}/advertiser-cpms`, request, REQUEST_CONFIG);
            return await this.fetchAdvertiser(advertiser.id);
        }
        catch (error) {
            throw new AdvertiserPricingSaveFailed(error);
        }
    }
    async deleteItem() {
        throw new NotImplementedError("delete");
    }
}
export default PricingAdvertisers;
//# sourceMappingURL=index.js.map